import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  FlatList,
  Image,
  Alert,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useAuth } from '../services/AuthContext';
import { getUserByClerkId, getUserById } from '../services/database';
import { getUserConversations, formatConversation, subscribeToMessages as subscribeToMessagesSupabase, deleteConversation } from '../services/chatService';
import { supabase } from '../services/supabase';
import { getProfilePictureUrl } from '../services/storage';
import { useFocusEffect } from '@react-navigation/native';
import { getColors, typography, spacing, borderRadius, getShadows, getComponents } from '../styles/theme';
import { useChatCache } from '../contexts/ChatCacheContext';
import { useTheme } from '../contexts/ThemeContext';

// Profile Image Component that handles Supabase storage URLs
const ProfileImage = ({ profilePictureUrl, accountType, size = 'thumbnail', colors }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triedFallback, setTriedFallback] = useState(false);

  useEffect(() => {
    const loadImage = () => {
      if (!profilePictureUrl) {
        setLoading(false);
        setImageUrl(null);
        return;
      }

      try {
        // Use Supabase storage helper (handles caching internally)
        const url = getProfilePictureUrl(profilePictureUrl, size);
        setImageUrl(url);
      } catch (error) {
        console.error('Error loading profile image:', error);
        setImageUrl(null);
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [profilePictureUrl, size]);

  const handleImageError = () => {
    if (!triedFallback && size !== 'original') {
      setTriedFallback(true);
      try {
        const fallbackUrl = getProfilePictureUrl(profilePictureUrl, 'original');
        setImageUrl(fallbackUrl);
      } catch (error) {
        setImageUrl(null);
      }
    } else {
      setImageUrl(null);
    }
  };

  if (loading) {
    return (
      <View style={[styles.avatar, styles.avatarPlaceholder]}>
        <Icon name="person" size={28} color={colors?.textLight || '#999'} />
      </View>
    );
  }

  if (!imageUrl || !profilePictureUrl) {
    return (
      <View style={[styles.avatar, styles.avatarPlaceholder]}>
        <Icon 
          name={accountType === 'CLUB' ? 'business' : 'person'} 
          size={28} 
          color={colors?.textLight || '#999'}
        />
      </View>
    );
  }

  return (
    <Image 
      source={{ uri: imageUrl }} 
      style={styles.avatar}
      resizeMode="cover"
      onError={handleImageError}
    />
  );
};

const ChatScreen = ({ navigation }) => {
  const { user, appUser, clerkUserId } = useAuth();
  const { cacheConversation } = useChatCache();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const shadows = getShadows(isDarkMode);
  const components = getComponents(isDarkMode);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserDbId, setCurrentUserDbId] = useState(null);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const subscriptionRef = useRef(null);

  useFocusEffect(
    React.useCallback(() => {
      const initializeChat = async () => {
        try {
          const dbId = await fetchCurrentUserDbId();
          if (dbId) {
            await fetchConversations(dbId);
            // Subscribe after we have the user DB ID
            subscribeToNewMessages();
          } else {
            // If we can't get the user DB ID, stop loading
            setLoading(false);
          }
        } catch (error) {
          console.error('Error initializing chat:', error);
          setLoading(false);
        }
      };
      
      initializeChat();
      
      return () => {
        if (subscriptionRef.current) {
          console.log('🧹 Cleaning up subscription');
          if (typeof subscriptionRef.current === 'function') {
            subscriptionRef.current();
          }
          subscriptionRef.current = null;
        }
      };
    }, [currentUserDbId])
  );

  // Re-subscribe when currentUserDbId becomes available
  useEffect(() => {
    if (currentUserDbId && !subscriptionRef.current) {
      console.log('🔄 Setting up subscription with user DB ID');
      subscribeToNewMessages();
    }
  }, [currentUserDbId]);

  // Update navigation badge when unread count changes
  useEffect(() => {
    navigation.setOptions({
      tabBarBadge: totalUnreadCount > 0 ? totalUnreadCount : null,
    });
  }, [totalUnreadCount, navigation]);

  const fetchCurrentUserDbId = async () => {
    try {
      // Use appUser.id directly if available (Supabase database ID)
      if (appUser?.id) {
        console.log('✅ Using appUser.id:', appUser.id);
        setCurrentUserDbId(appUser.id);
        return appUser.id;
      }

      // Fallback: fetch by Clerk ID if appUser not loaded yet
      if (clerkUserId) {
        console.log('🔄 Fetching user by Clerk ID:', clerkUserId);
        const dbUser = await getUserByClerkId(clerkUserId);
        if (dbUser?.id) {
          console.log('✅ Fetched user DB ID:', dbUser.id);
          setCurrentUserDbId(dbUser.id);
          return dbUser.id;
        }
      }

      console.warn('⚠️ No user DB ID available');
      return null;
    } catch (error) {
      console.error('Error fetching current user DB ID:', error);
      return null;
    }
  };

  const fetchConversations = async (dbIdOverride = null, isBackgroundRefresh = false) => {
    try {
      // Only show loading spinner on initial load, not background refreshes
      if (!isBackgroundRefresh) {
        setLoading(true);
      }

      const userDbId = dbIdOverride || currentUserDbId;

      if (!userDbId) {
        console.log('⏳ Waiting for currentUserDbId before fetching conversations...');
        setLoading(false);
        return;
      }

      console.log('📥 Fetching conversations for user:', userDbId);

      // Use Supabase to get conversations (properly filtered by user)
      const supabaseConversations = await getUserConversations(userDbId);
      console.log('📨 Fetched conversations from Supabase:', supabaseConversations.length);

      // Format conversations and get unread counts
      const conversationsWithUserData = await Promise.all(
        supabaseConversations.map(async (conv) => {
          // Format the conversation (converts snake_case to camelCase)
          const formatted = formatConversation(conv, userDbId);
          
          if (!formatted.otherUser) {
            console.warn('⚠️ Conversation missing otherUser:', conv.id);
            return null;
          }

          // Get unread count for this conversation
          const { count, error: unreadError } = await supabase
            .from('messages')
            .select('id', { count: 'exact', head: true })
            .eq('conversation_id', conv.id)
            .eq('receiver_id', userDbId)
            .eq('read', false);

          if (unreadError) {
            console.error('Error fetching unread count:', unreadError);
          }

          const unreadCount = count || 0;

          // Get latest messages for caching
          const { data: messages } = await supabase
            .from('messages')
            .select('*')
            .eq('conversation_id', conv.id)
            .order('created_at', { ascending: false })
            .limit(50);

          // Cache this conversation
          if (messages && messages.length > 0) {
            const formattedMessages = messages.reverse().map(msg => ({
              id: msg.id,
              conversationId: msg.conversation_id,
              senderId: msg.sender_id,
              receiverId: msg.receiver_id,
              content: msg.content,
              createdAt: msg.created_at,
              read: msg.read,
            }));

            cacheConversation(conv.id, {
              messages: formattedMessages,
              otherUser: formatted.otherUser,
            });
          }

          return {
            id: formatted.id,
            lastMessageAt: formatted.lastMessageAt,
            otherUser: formatted.otherUser,
            unreadCount,
            latestMessage: formatted.latestMessage,
          };
        })
      );

      // Filter out null conversations and sort
      const validConversations = conversationsWithUserData.filter(Boolean);
      const sorted = validConversations.sort(
        (a, b) => new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0)
      );

      const totalUnread = sorted.reduce((sum, conv) => sum + conv.unreadCount, 0);
      
      console.log('✅ Loaded conversations:', {
        count: sorted.length,
        totalUnread,
      });

      setConversations(sorted);
      setTotalUnreadCount(totalUnread);
    } catch (error) {
      console.error('❌ Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToNewMessages = () => {
    // Only subscribe if we have the current user DB ID
    if (!currentUserDbId) {
      console.log('⏳ Waiting for currentUserDbId before subscribing...');
      return;
    }

    // Clean up existing subscription if any
    if (subscriptionRef.current) {
      console.log('🧹 Cleaning up existing subscription');
      if (typeof subscriptionRef.current === 'function') {
        subscriptionRef.current();
      }
      subscriptionRef.current = null;
    }

    try {
      // Use Supabase real-time subscription for new messages
      const unsubscribeMessages = subscribeToMessagesSupabase(currentUserDbId, (newMessage) => {
        console.log('🔔 New message received via Supabase subscription');
        // Background refresh (no loading spinner)
        fetchConversations(currentUserDbId, true);
      });

      // Also subscribe to read status updates to refresh unread counts
      const readStatusChannel = supabase
        .channel(`read-status-updates-${currentUserDbId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'messages',
            filter: `receiver_id=eq.${currentUserDbId}`,
          },
          (payload) => {
            if (payload.new.read !== payload.old.read && payload.new.read === true) {
              console.log('📖 Message marked as read, refreshing unread counts');
              // Background refresh (no loading spinner)
              fetchConversations(currentUserDbId, true);
            }
          }
        )
        .subscribe();

      // Return a cleanup function that unsubscribes from both
      subscriptionRef.current = () => {
        unsubscribeMessages();
        supabase.removeChannel(readStatusChannel);
      };

      console.log('✅ Successfully subscribed to new messages via Supabase');
    } catch (error) {
      console.error('❌ Error setting up Supabase subscription:', error);
      subscriptionRef.current = null;
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    Alert.alert(
      'Delete Conversation',
      'Are you sure you want to delete this conversation? This action cannot be undone.',
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              // Use Supabase to delete (CASCADE will remove messages and conversation_users)
              await deleteConversation(conversationId);

              // Remove from local state
              setConversations(prev => prev.filter(conv => conv.id !== conversationId));
              
              // Update unread count
              const remainingUnread = conversations
                .filter(conv => conv.id !== conversationId)
                .reduce((sum, conv) => sum + conv.unreadCount, 0);
              setTotalUnreadCount(remainingUnread);
              
              console.log('✅ Conversation deleted successfully');
            } catch (error) {
              console.error('Error deleting conversation:', error);
              Alert.alert('Error', 'Failed to delete conversation. Please try again.');
            }
          },
        },
      ]
    );
  };

  const renderConversation = ({ item }) => {
    const lastMessage = item.latestMessage;
    const preview = lastMessage?.content || 'No messages yet';
    
    // Format time more elegantly
    const time = lastMessage?.createdAt 
      ? formatMessageTime(new Date(lastMessage.createdAt))
      : '';

    const otherUser = item.otherUser;
    const displayName = otherUser 
      ? (otherUser.accountType === 'CLUB' ? otherUser.clubName : otherUser.username)
      : 'Unknown User';

    const hasUnread = item.unreadCount > 0;
    
    console.log(`🎨 Rendering conversation ${item.id.substring(0, 8)}: hasUnread=${hasUnread}, count=${item.unreadCount}`);

    return (
      <View style={styles.conversationItemWrapper}>
        <TouchableOpacity
          style={styles.conversationItem}
          onPress={() => navigation.navigate('ChatDetail', { 
            conversationId: item.id,
            otherUserId: otherUser?.id,
            otherUser: otherUser,
          })}
          activeOpacity={0.7}
        >
          <View style={styles.avatarContainer}>
            <ProfileImage 
              profilePictureUrl={otherUser?.profilePictureUrl}
              accountType={otherUser?.accountType}
              colors={colors}
            />
            {hasUnread && <View style={styles.onlineIndicator} />}
          </View>
          
          <View style={styles.conversationContent}>
            <View style={styles.conversationHeader}>
              <Text style={styles.conversationName} numberOfLines={1}>
                {displayName}
              </Text>
              <Text style={styles.conversationTime}>{time}</Text>
            </View>
            
            <View style={styles.previewRow}>
              <Text 
                style={[
                  styles.conversationPreview, 
                  hasUnread && styles.conversationPreviewUnread
                ]} 
                numberOfLines={1}
              >
                {preview}
              </Text>
              {hasUnread && (
                <View style={styles.unreadBadge}>
                  <Text style={styles.unreadBadgeText}>{item.unreadCount}</Text>
                </View>
              )}
            </View>
          </View>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={styles.deleteButton}
          onPress={() => handleDeleteConversation(item.id)}
          activeOpacity={0.7}
        >
          <Icon name="delete" size={20} color={colors.error} />
        </TouchableOpacity>
      </View>
    );
  };

  const formatMessageTime = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const styles = getStyles(colors, shadows, components);

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <LoadingAnimation size={525} />
        </View>
      </SafeAreaView>
    );
  }

  if (conversations.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.content}>
          <View style={styles.emptyState}>
            <Icon name="chat" size={80} color={colors.textLight} />
            <Text style={styles.emptyTitle}>Start Chatting</Text>
            <Text style={styles.emptySubtitle}>
              Chats will be here
            </Text>
            
            <TouchableOpacity 
              style={styles.findButton}
              onPress={() => navigation.navigate('Map')}
            >
              <Icon name="map" size={20} color="white" />
              <Text style={styles.findButtonText}>Find Players</Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Messages</Text>
      </View>
      <FlatList
        data={conversations}
        renderItem={renderConversation}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.list}
      />
    </SafeAreaView>
  );
};

// Dynamic styles based on theme
const getStyles = (colors, shadows, components) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.xl,
  },
  header: {
    backgroundColor: colors.surface,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
    ...shadows.small,
  },
  headerTitle: {
    fontSize: typography.h2,
    fontWeight: typography.bold,
    color: colors.text,
  },
  list: {
    backgroundColor: colors.surface,
  },
  conversationItemWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  conversationItem: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    flex: 1,
  },
  deleteButton: {
    padding: spacing.md,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  avatarContainer: {
    position: 'relative',
    marginRight: spacing.md,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
  },
  avatarPlaceholder: {
    backgroundColor: colors.backgroundDark,
    alignItems: 'center',
    justifyContent: 'center',
  },
  onlineIndicator: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: colors.primary,
    borderWidth: 2,
    borderColor: colors.surface,
  },
  conversationContent: {
    flex: 1,
    justifyContent: 'center',
  },
  conversationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  conversationName: {
    fontSize: typography.body,
    fontWeight: typography.semiBold,
    color: colors.text,
    flex: 1,
    marginRight: spacing.sm,
  },
  conversationTime: {
    fontSize: typography.caption,
    color: colors.textSecondary,
    fontWeight: typography.medium,
  },
  previewRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  conversationPreview: {
    fontSize: typography.bodySmall,
    color: colors.textSecondary,
    flex: 1,
    marginRight: spacing.sm,
  },
  conversationPreviewUnread: {
    color: colors.text,
    fontWeight: typography.semiBold,
  },
  unreadBadge: {
    backgroundColor: colors.primary,
    borderRadius: borderRadius.pill,
    minWidth: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.xs,
  },
  unreadBadgeText: {
    color: colors.textInverse,
    fontSize: typography.caption,
    fontWeight: typography.bold,
  },
  emptyState: {
    alignItems: 'center',
  },
  emptyTitle: {
    fontSize: typography.h2,
    fontWeight: typography.bold,
    color: colors.text,
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
  },
  emptySubtitle: {
    fontSize: typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: typography.lineHeightRelaxed * typography.body,
    marginBottom: spacing.xl,
    paddingHorizontal: spacing.lg,
  },
  findButton: {
    ...components.button.primary,
    flexDirection: 'row',
    gap: spacing.sm,
  },
  findButtonText: {
    ...components.buttonText.primary,
  },
});

export default ChatScreen;
